/**
 * KraftStories Analytics Events
 * Centralized dataLayer + optional gtag mirroring for GA4 (GTM and direct gtag.js).
 * The gtag mirror runs ONLY when GTM is not configured — gated on
 * window.__kraft_mirror_gtag_to_ga4 (set by gtm-datalayer.liquid, same flag the
 * Liquid uses). With a GTM container set, GTM already forwards each dataLayer
 * event to GA4, so also calling gtag here would double-count. Mirror is the
 * no-GTM fallback path only.
 */

(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var GA4_MAX_STR = 100;

  /** Shallow GA4 param cleanup: drop null/undefined, truncate long strings; sanitize item rows. */
  function sanitizeGa4Params(obj) {
    if (!obj || typeof obj !== 'object') return {};
    var out = {};
    Object.keys(obj).forEach(function (k) {
      var v = obj[k];
      if (v === null || v === undefined) return;
      if (typeof v === 'string') {
        out[k] = v.length > GA4_MAX_STR ? v.substring(0, GA4_MAX_STR) : v;
      } else if (k === 'items' && Array.isArray(v)) {
        out[k] = v.map(function (item) {
          if (!item || typeof item !== 'object') return item;
          var o = {};
          Object.keys(item).forEach(function (ik) {
            var iv = item[ik];
            if (iv === null || iv === undefined) return;
            if (typeof iv === 'string') {
              o[ik] = iv.length > GA4_MAX_STR ? iv.substring(0, GA4_MAX_STR) : iv;
            } else {
              o[ik] = iv;
            }
          });
          return o;
        });
      } else if (typeof v === 'object' && !Array.isArray(v)) {
        out[k] = v;
      } else {
        out[k] = v;
      }
    });
    return out;
  }

  /**
   * Events the GTM container (GTM-WPXPSPHD) has its own GA4 tags for. Mirroring these
   * via gtag as well would double-count them.
   *
   * Everything else the theme pushes is NOT forwarded by GTM and reaches GA4 only through
   * the gtag mirror below. Verified against GA4 either side of 20 Jul 2026, when the
   * blanket `__kraft_mirror_gtag_to_ga4` guard shipped: these three kept flowing
   * (add_to_cart 157->111, begin_checkout 153->140, email_click 45->41) while every other
   * theme event flatlined (nav_menu_hover 685->0, select_item 643->13, location_change
   * 184->1, view_cart 83->0, cta_click 33->0, remove_from_cart 18->0, modal_open 6->0,
   * plus all form_ events, generate_lead and signup_popup_submit).
   *
   * Keep this list in sync with the GA4 event tags configured in GTM. If you add a GA4 tag
   * in GTM for another event, add it here too or it will be counted twice.
   */
  var GTM_FORWARDED_EVENTS = {
    add_to_cart: true,
    begin_checkout: true,
    email_click: true
  };

  function mirrorGtagFlat(eventName, params) {
    if (typeof window.gtag !== 'function') return;
    // No GTM container (__kraft_mirror_gtag_to_ga4 === true): gtag is the only path, mirror everything.
    // GTM present: mirror everything EXCEPT the events GTM already forwards itself.
    if (window.__kraft_mirror_gtag_to_ga4 !== true && GTM_FORWARDED_EVENTS[eventName] === true) return;
    var clean = sanitizeGa4Params(params || {});
    window.gtag('event', eventName, clean);
  }

  function pushEvent(eventName, data) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(Object.assign({ event: eventName }, data));
    if (data && data.ecommerce && typeof window.gtag === 'function') {
      var ec = data.ecommerce;
      var flat = {};
      Object.keys(data).forEach(function (key) {
        if (key !== 'ecommerce' && key !== 'event') flat[key] = data[key];
      });
      if (ec.currency != null) flat.currency = ec.currency;
      if (ec.value != null) flat.value = ec.value;
      if (ec.items != null) flat.items = ec.items;
      if (ec.item_list_id != null) flat.item_list_id = ec.item_list_id;
      if (ec.item_list_name != null) flat.item_list_name = ec.item_list_name;
      mirrorGtagFlat(eventName, flat);
    }
  }

  function pushNonEcommerceEvent(eventName, data) {
    var raw = data || {};
    window.dataLayer.push(Object.assign({ event: eventName }, raw));
    mirrorGtagFlat(eventName, raw);
  }

  /**
   * Public: dataLayer push + gtag mirror (non-ecommerce). Used by modal-form-handler, kraft-search-sort.
   */
  window.__kraftGa4Mirror = function (eventName, payload) {
    pushNonEcommerceEvent(eventName, payload || {});
  };

  function getShopCurrency() {
    return window.__kraft_analytics_currency || 'USD';
  }

  /** Page segmentation from gtm-datalayer.liquid (optional on every event). */
  function getPageSegmentation() {
    var out = {};
    if (window.__kraft_page_category) {
      out.page_category = window.__kraft_page_category;
    }
    if (window.__kraft_page_location_city) {
      out.page_location_city = window.__kraft_page_location_city;
    }
    return out;
  }

  function getLineProperty(properties, key) {
    if (!properties) return '';
    if (!Array.isArray(properties) && typeof properties === 'object') {
      return properties[key] || '';
    }
    if (Array.isArray(properties)) {
      for (var i = 0; i < properties.length; i += 1) {
        var prop = properties[i];
        if (!prop) continue;
        if (Array.isArray(prop) && prop[0] === key) return prop[1] || '';
        if (prop.name === key || prop.key === key || prop.first === key) {
          return prop.value || prop.last || '';
        }
      }
    }
    return '';
  }

  function addBookingContext(item, context) {
    if (!context) return item;
    [
      'booking_location',
      'booking_location_name',
      'workshop_name',
      'workshop_metaobject_id',
      'workshop_address',
      'page_location_city',
    ].forEach(function (key) {
      if (context[key]) item[key] = context[key];
    });
    return item;
  }

  function getPrimaryItemContext(items) {
    var source = (items || []).find(function (item) {
      return item && (item.booking_location || item.booking_location_name || item.workshop_name);
    });
    if (!source) return {};
    var out = {};
    [
      'booking_location',
      'booking_location_name',
      'workshop_name',
      'workshop_metaobject_id',
      'workshop_address',
      'page_location_city',
    ].forEach(function (key) {
      if (source[key]) out[key] = source[key];
    });
    return out;
  }

  function withEcommerceContext(payload, items) {
    return Object.assign({}, getPageSegmentation(), getPrimaryItemContext(items), payload);
  }

  /**
   * Build a GA4 items array entry from a cart line item response object
   * (as returned by /cart/add.js or /cart.js).
   */
  function buildItemFromCartResponse(item) {
    var props = item.properties || {};
    var context = {
      booking_location: getLineProperty(props, '_booking_location'),
      booking_location_name: getLineProperty(props, '_booking_location_name'),
      workshop_name: getLineProperty(props, '_workshop_name'),
      workshop_metaobject_id: getLineProperty(props, '_workshop_metaobject_id'),
      workshop_address: getLineProperty(props, '_workshop_address'),
      page_location_city: getLineProperty(props, '_page_location_city') || window.__kraft_page_location_city || '',
    };

    return addBookingContext({
      item_id: String(item.product_id),
      item_name: item.product_title || item.title,
      item_brand: item.vendor || '',
      item_category: item.product_type || '',
      item_variant: item.variant_title || '',
      price: (item.final_price || item.price || 0) / 100,
      quantity: item.quantity || 1,
    }, context);
  }

  /**
   * Build a GA4 items entry from product data- attributes on a DOM element.
   */
  function buildItemFromElement(el) {
    return addBookingContext({
      item_id: el.dataset.productId || '',
      item_name: el.dataset.productName || '',
      item_brand: el.dataset.productBrand || '',
      item_category: el.dataset.productCategory || '',
      item_variant: el.dataset.productVariant || '',
      price: parseFloat(el.dataset.productPrice) || 0,
      quantity: 1,
    }, {
      booking_location: el.dataset.bookingLocation || '',
      booking_location_name: el.dataset.bookingLocationName || '',
      workshop_name: el.dataset.workshopName || '',
      workshop_metaobject_id: el.dataset.workshopMetaobjectId || '',
      workshop_address: el.dataset.workshopAddress || '',
      page_location_city: el.dataset.pageLocationCity || window.__kraft_page_location_city || '',
    });
  }

  // ─── ADD TO CART ──────────────────────────────────────────────────

  var lastThemeCartAddAt = 0;

  /**
   * Handles the theme:cart:add custom event dispatched by product-form-drawer.js.
   * The event.detail.data is the /cart/add.js JSON response.
   */
  function handleCartAdd(event) {
    var data = event.detail && event.detail.data;
    if (!data) return;
    lastThemeCartAddAt = Date.now();

    var items = [];
    if (Array.isArray(data.items)) {
      data.items.forEach(function (item) {
        items.push(buildItemFromCartResponse(item));
      });
    } else {
      items.push(buildItemFromCartResponse(data));
    }

    var totalValue = items.reduce(function (sum, i) {
      return sum + i.price * i.quantity;
    }, 0);

    pushEvent('add_to_cart', withEcommerceContext({
      ecommerce: {
        currency: getShopCurrency(),
        value: totalValue,
        items: items,
      },
    }, items));
  }

  document.addEventListener('theme:cart:add', handleCartAdd);

  // Also capture the module-based CartAddEvent (fires as 'cart:update' with source info)
  document.addEventListener('cart:update', function (event) {
    var detail = event.detail || {};
    var data = detail.data || {};
    if (data.didError) return;
    if (data.source !== 'product-form-component' && data.source !== 'quick-add') return;
    if (Date.now() - lastThemeCartAddAt < 1000) return;

    var variantId = String(detail.sourceId || data.variantId || '');
    var addedQuantity = Number(data.itemCount) || 1;

    setTimeout(function () {
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart.items || cart.items.length === 0) return;

          var addedLine = cart.items.find(function (item) {
            return String(item.variant_id || '') === variantId;
          });
          if (!addedLine) return;

          var ga4Item = buildItemFromCartResponse(addedLine);
          ga4Item.quantity = addedQuantity;

          pushEvent('add_to_cart', withEcommerceContext({
            ecommerce: {
              currency: getShopCurrency(),
              value: ga4Item.price * ga4Item.quantity,
              items: [ga4Item],
            },
          }, [ga4Item]));
        })
        .catch(function () {});
    }, 250);
  });

  // ─── REMOVE FROM CART ─────────────────────────────────────────────

  /**
   * Intercept clicks on cart item remove buttons and quantity decrease buttons.
   * We fetch the cart before the action to compare.
   */
  (function () {
    var preActionCart = null;

    function capturePreActionCart() {
      return fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { preActionCart = cart; })
        .catch(function () { preActionCart = null; });
    }

    document.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('.cart-items__remove');
      if (!removeBtn) return;

      capturePreActionCart();

      setTimeout(function () {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (newCart) {
            if (!preActionCart) return;

            var oldIds = {};
            preActionCart.items.forEach(function (item) {
              oldIds[item.key] = item;
            });

            var newIds = {};
            newCart.items.forEach(function (item) {
              newIds[item.key] = item;
            });

            Object.keys(oldIds).forEach(function (key) {
              if (!newIds[key]) {
                var removed = oldIds[key];
                pushEvent('remove_from_cart', withEcommerceContext({
                  ecommerce: {
                    currency: getShopCurrency(),
                    value: (removed.final_price / 100) * removed.quantity,
                    items: [buildItemFromCartResponse(removed)],
                  },
                }, [buildItemFromCartResponse(removed)]));
              }
            });

            preActionCart = null;
          })
          .catch(function () { preActionCart = null; });
      }, 800);
    });
  })();

  // ─── VIEW CART (drawer open) ──────────────────────────────────────

  (function () {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type === 'attributes' && m.attributeName === 'open') {
          var dialog = m.target;
          if (dialog.hasAttribute('open') && dialog.closest('cart-drawer-component')) {
            fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                if (!cart.items || cart.items.length === 0) return;

                var items = cart.items.map(function (item) {
                  return buildItemFromCartResponse(item);
                });

                var totalValue = items.reduce(function (sum, i) {
                  return sum + i.price * i.quantity;
                }, 0);

                pushEvent('view_cart', withEcommerceContext({
                  ecommerce: {
                    currency: getShopCurrency(),
                    value: totalValue,
                    items: items,
                  },
                }, items));
              })
              .catch(function () {});
          }
        }
      });
    });

    function observeCartDrawer() {
      var drawerDialog = document.querySelector('cart-drawer-component dialog');
      if (drawerDialog) {
        observer.observe(drawerDialog, { attributes: true, attributeFilter: ['open'] });
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeCartDrawer);
    } else {
      observeCartDrawer();
    }
  })();

  // ─── SELECT ITEM (product card click) ─────────────────────────────

  document.addEventListener('click', function (e) {
    var card = e.target.closest('[data-analytics-product]');
    if (!card) return;

    var item = buildItemFromElement(card);
    if (!item.item_id) return;

    var selectedItems = [Object.assign({}, item, {
      item_list_id: card.dataset.listId || '',
      item_list_name: card.dataset.listName || '',
      index: parseInt(card.dataset.productIndex, 10) || 0,
    })];

    pushEvent('select_item', withEcommerceContext({
      ecommerce: {
        items: selectedItems,
      },
    }, selectedItems));
  });

  // ─── BEGIN CHECKOUT ───────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var checkoutBtn = e.target.closest('#checkout, [name="checkout"]');
    if (!checkoutBtn) return;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) return;

        var items = cart.items.map(function (item) {
          return buildItemFromCartResponse(item);
        });

        var totalValue = items.reduce(function (sum, i) {
          return sum + i.price * i.quantity;
        }, 0);

        pushEvent('begin_checkout', withEcommerceContext({
          ecommerce: {
            currency: getShopCurrency(),
            value: totalValue,
            items: items,
          },
        }, items));
      })
      .catch(function () {});
  });

  // ─── SEARCH ───────────────────────────────────────────────────────

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.getAttribute('action') !== '/search') return;

    var input = form.querySelector('input[name="q"]');
    if (!input || !input.value.trim()) return;

    pushNonEcommerceEvent('search', Object.assign({
      search_term: input.value.trim(),
    }, getPageSegmentation()));
  });

  // ─── SIGN UP (newsletter email signup) ────────────────────────────

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form) return;

    var isEmailSignup =
      form.classList.contains('email-signup__form') ||
      form.closest('email-signup-block');
    if (!isEmailSignup) return;

    var emailInput = form.querySelector('input[name="contact[email]"]');
    if (!emailInput || !emailInput.value.trim()) return;

    pushNonEcommerceEvent('sign_up', Object.assign({
      method: 'newsletter',
    }, getPageSegmentation()));
  });

  // ─── MODAL OPEN ───────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-modal-trigger]');
    if (!trigger) return;

    var modalId = trigger.getAttribute('data-modal-trigger');
    pushNonEcommerceEvent('modal_open', Object.assign({
      modal_id: modalId,
    }, getPageSegmentation()));
  });

  // ─── LOCATION CHANGE ─────────────────────────────────────────────

  document.addEventListener('locationChange', function (e) {
    var detail = e.detail || {};
    pushNonEcommerceEvent('location_change', Object.assign({
      location: detail.location || '',
      location_name: detail.locationName || '',
    }, getPageSegmentation()));
  });

  // ─── PHONE CLICK ─────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href^="tel:"]');
    if (!link) return;
    pushNonEcommerceEvent('phone_click', Object.assign({
      phone_number: (link.getAttribute('href') || '').replace(/^tel:/i, ''),
      link_text: (link.textContent || '').trim(),
      page_path: window.location.pathname,
    }, getPageSegmentation()));
  });

  // ─── EMAIL CLICK ───────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href^="mailto:"]');
    if (!link) return;
    var href = link.getAttribute('href') || '';
    var email = href.replace(/^mailto:/i, '').split('?')[0];
    pushNonEcommerceEvent('email_click', Object.assign({
      email_address: email,
      link_text: (link.textContent || '').trim(),
      page_path: window.location.pathname,
    }, getPageSegmentation()));
  });

  // ─── CTA CLICK (.simple_button, excludes modal + checkout) ───────

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.simple_button');
    if (!btn) return;
    if (btn.hasAttribute('data-modal-trigger')) return;
    if (btn.closest('#checkout') || btn.getAttribute('name') === 'checkout') return;
    var href = btn.getAttribute('href') || '';
    if (href.indexOf('javascript:') === 0) return;

    pushNonEcommerceEvent('cta_click', Object.assign({
      cta_text: (btn.textContent || '').trim().substring(0, 100),
      cta_url: href,
      page_path: window.location.pathname,
    }, getPageSegmentation()));
  });

  // ─── OUTBOUND LINK CLICK ─────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href]');
    if (!link) return;
    var raw = link.getAttribute('href') || '';
    if (raw.indexOf('tel:') === 0 || raw.indexOf('mailto:') === 0) return;
    if (raw.indexOf('#') === 0 || raw.indexOf('javascript:') === 0) return;
    try {
      var url = new URL(link.href, window.location.origin);
      if (url.hostname === window.location.hostname) return;
    } catch (err) {
      return;
    }

    pushNonEcommerceEvent('outbound_click', Object.assign({
      link_url: link.href,
      link_domain: link.hostname,
      link_text: (link.textContent || '').trim().substring(0, 100),
      page_path: window.location.pathname,
    }, getPageSegmentation()));
  });

  // ─── NAV MENU HOVER / TAP ────────────────────────────────────────

  (function () {
    var HOVER_THRESHOLD_MS = 200;
    var isHoverDevice = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    function getMenuItemName(el) {
      var span = el.querySelector('span');
      return ((span ? span.textContent : el.textContent) || '').trim();
    }

    function getMenuItemType(li) {
      return li.querySelector('[data-mega-trigger]') ? 'mega_menu' : 'nav_link';
    }

    function fireHoverEvent(name, type, method) {
      pushNonEcommerceEvent('nav_menu_hover', Object.assign({
        menu_item_name: name,
        menu_item_type: type,
        interaction_method: method,
        page_path: window.location.pathname,
      }, getPageSegmentation()));
    }

    function initNavHoverTracking() {
      var navItems = document.querySelectorAll('.navbar-nav > .nav-item');
      if (!navItems.length) return;

      navItems.forEach(function (li) {
        var trigger = li.querySelector('[data-mega-trigger]') || li.querySelector('.nav-link');
        if (!trigger) return;

        var hoverTimer = null;
        var firedThisSession = false;

        li.addEventListener('mouseenter', function () {
          if (!isHoverDevice) return;
          firedThisSession = false;
          hoverTimer = setTimeout(function () {
            hoverTimer = null;
            if (firedThisSession) return;
            firedThisSession = true;
            fireHoverEvent(getMenuItemName(trigger), getMenuItemType(li), 'hover');
          }, HOVER_THRESHOLD_MS);
        });

        li.addEventListener('mouseleave', function () {
          if (hoverTimer) {
            clearTimeout(hoverTimer);
            hoverTimer = null;
          }
          firedThisSession = false;
        });

        var megaTrigger = li.querySelector('[data-mega-trigger]');
        if (megaTrigger) {
          megaTrigger.addEventListener('click', function () {
            if (isHoverDevice) return;
            fireHoverEvent(getMenuItemName(megaTrigger), 'mega_menu', 'click');
          });
        }
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initNavHoverTracking);
    } else {
      initNavHoverTracking();
    }
  })();

  // ─── HERO META BADGE: RATING CLICK + ADDRESS HOVER / TAP ─────────

  (function () {
    var HOVER_DWELL_MS = 200;

    function initHeroMetaBadgeTracking() {
      var badges = document.querySelectorAll('.hero-meta-badge');
      if (!badges.length) return;

      var path = window.location.pathname;
      var isHoverFine =
        window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      var isCoarseOrNoHover =
        window.matchMedia('(hover: none), (pointer: coarse)').matches;

      badges.forEach(function (badge) {
        badge.addEventListener('click', function (e) {
          var link = e.target.closest('a.hero-meta-badge__rating');
          if (!link || !badge.contains(link)) return;
          var valEl = link.querySelector('.hero-meta-badge__rating-value');
          pushNonEcommerceEvent(
            'kraft_hero_meta_rating_click',
            Object.assign(
              {
                rating_display: (valEl && valEl.textContent
                  ? valEl.textContent
                  : ''
                ).trim(),
                link_href: link.getAttribute('href') || '',
                page_path: path,
              },
              getPageSegmentation()
            )
          );
        });

        badge.querySelectorAll('.address-tooltip-trigger').forEach(function (trigger) {
          var hoverTimer = null;

          function markEngaged(key) {
            if (trigger.dataset.kraftHeroAddressEngaged) return false;
            trigger.dataset.kraftHeroAddressEngaged = key;
            return true;
          }

          trigger.addEventListener('mouseenter', function () {
            if (!isHoverFine) return;
            hoverTimer = setTimeout(function () {
              hoverTimer = null;
              if (!markEngaged('hover')) return;
              pushNonEcommerceEvent(
                'kraft_hero_meta_address_hover',
                Object.assign(
                  {
                    engagement_method: 'hover_dwell',
                    page_path: path,
                  },
                  getPageSegmentation()
                )
              );
            }, HOVER_DWELL_MS);
          });

          trigger.addEventListener('mouseleave', function () {
            if (hoverTimer) {
              clearTimeout(hoverTimer);
              hoverTimer = null;
            }
          });

          if (isHoverFine) {
            trigger.addEventListener('keydown', function (e) {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              if (!markEngaged('keyboard')) return;
              pushNonEcommerceEvent(
                'kraft_hero_meta_address_hover',
                Object.assign(
                  {
                    engagement_method: 'keyboard_activate',
                    page_path: path,
                  },
                  getPageSegmentation()
                )
              );
            });
          }

          if (isCoarseOrNoHover) {
            trigger.addEventListener(
              'click',
              function () {
                if (!markEngaged('tap')) return;
                pushNonEcommerceEvent(
                  'kraft_hero_meta_address_hover',
                  Object.assign(
                    {
                      engagement_method: 'tap_open',
                      page_path: path,
                    },
                    getPageSegmentation()
                  )
                );
              },
              true
            );
          }
        });
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initHeroMetaBadgeTracking);
    } else {
      initHeroMetaBadgeTracking();
    }
  })();

  // ─── METAOBJECT LOCATION: SCROLL TO TOP CLICK ─────────────────────

  (function () {
    function initMetaobjectScrollTopTracking() {
      var btn = document.getElementById('kraft-metaobject-scroll-top');
      if (!btn) return;
      var path = window.location.pathname;
      btn.addEventListener('click', function () {
        pushNonEcommerceEvent(
          'kraft_metaobject_scroll_top_click',
          Object.assign(
            {
              page_path: path,
            },
            getPageSegmentation()
          )
        );
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initMetaobjectScrollTopTracking);
    } else {
      initMetaobjectScrollTopTracking();
    }
  })();

  // ─── EXPOSE CURRENCY FOR JS-ONLY CONTEXTS ────────────────────────

  if (typeof Theme !== 'undefined' && Theme.routes) {
    // Currency is set by gtm-datalayer.liquid, but provide a fallback
  }
})();
